<template>
  <div
    :class="{ 'bg-white': !isAuthenticated || hideSideBar }"
    class="flex flex-col full-height"
  >
    <sidebar
      v-if="isAuthenticated && !hideSideBar"
      v-model="sidebarCollapsed"
    />

    <div
      class="full-height main-content"
      :class="{
        'offset': !sidebarCollapsed && isAuthenticated && !hideSideBar,
        'sidebar-width': sidebarCollapsed && isAuthenticated && !hideSideBar,
      }"
    >
      <b-notification
        v-if="showNotification && latestNews"
        aria-close-label="Close notification"
        class="mt-6 mx-6"
        role="alert"
        type="is-warning"
        @close="onCloseNotification"
      >
        <span>
          <span class="text-black" v-html="extractedContent"></span>
          <br />
          <a
            v-if="latestNews.link"
            :href="latestNews.link"
            target="_blank"
            rel="noopener noreferrer"
            class="notification-link"
          >前往通知 →</a>
        </span>
      </b-notification>
      <router-view />
    </div>
  </div>
</template>

<script>
import dayjs from 'dayjs';
import { mapGetters, mapState } from 'vuex';

import {
  Sidebar,
} from '@/components';

const DISMISSED_NEWS_KEY = 'dismissed_news_ids';

export default {
  name: 'App',
  components: {
    Sidebar,
  },
  data() {
    return {
      sidebarCollapsed: false,
      hideSideBarPath: [
        '/reset-password-verification',
        '/temp-report',
      ],
    };
  },
  computed: {
    ...mapGetters('user', [
      'isAuthenticated',
    ]),
    ...mapState('dashboard', {
      news: ({ news }) => news,
    }),
    hideSideBar() {
      return this.hideSideBarPath.some((path) => this.$route?.path.includes(path));
    },
    showNotification() {
      const currentPath = this.$route?.path;
      return currentPath === '/order/list' || currentPath === '/dashboard';
    },
    latestNews() {
      if (!this.news || this.news.length === 0) return null;

      const fourteenDaysAgo = dayjs().subtract(14, 'day');
      const dismissedId = this.getDismissedNewsId();

      // 篩選條件：1. 14天內發佈 2. 標題包含"【發佈紀錄】"
      const filteredNews = this.news.filter((article) => {
        const isWithin14Days = dayjs(article.date).isAfter(fourteenDaysAgo);
        const hasReleaseNoteTag = article.title && article.title.includes('【發佈紀錄】');

        return isWithin14Days && hasReleaseNoteTag;
      });
      const firstArticle = filteredNews.length > 0 ? filteredNews[0] : null;
      if (firstArticle && String(firstArticle.id) === String(dismissedId)) {
        return null;
      }
      return firstArticle;
    },
    extractedContent() {
      if (!this.latestNews || !this.latestNews.content) return '';

      const { content } = this.latestNews;
      // 提取 <blockquote> 標籤中的內容
      const blockquoteMatch = content.match(/<blockquote[^>]*>([\s\S]*?)<\/blockquote>/i);
      if (!blockquoteMatch) return '';

      const html = blockquoteMatch[1];
      // 提取 <p> 標籤中的純文字內容
      const pMatch = html.match(/<p[^>]*>([\s\S]*?)<\/p>/i);
      const text = pMatch ? pMatch[1].replace(/<[^>]*>/g, '').trim() : html.replace(/<[^>]*>/g, '').trim();

      if (text.length > 100) {
        return `${text.substring(0, 100)}...`;
      }
      return text;
    },
  },
  methods: {
    getDismissedNewsId() {
      try {
        return localStorage.getItem(DISMISSED_NEWS_KEY) || null;
      } catch (error) {
        return null;
      }
    },
    saveDismissedNewsId(id) {
      try {
        localStorage.setItem(DISMISSED_NEWS_KEY, id);
      } catch (error) {
        console.error('Failed to save dismissed news id:', error);
      }
    },
    onCloseNotification() {
      if (this.latestNews && this.latestNews.id) {
        this.saveDismissedNewsId(this.latestNews.id);
      }
    },
  },
};
</script>

<style>
html, body {
  background-color: #fafafa;
  height: 100%;
}
</style>

<style lang="scss" scoped>
.main-content {
  transition: margin 0.3s ease;

  &.offset {
    margin-left: 220px;
  }
  &.sidebar-width {
    margin-left: 50px;
  }
}

.notification-link {
  color: #2097F6 !important;
  text-decoration: none !important;

  &:hover {
    color: #4169E1 !important;
  }
}
</style>
